// boardDirectory — ONE thin, shared list of board cards for the always-on
// consumers (FEATURES.liveV2Board only).
//
// Three things mounted for the whole session each used to hold their own
// whole-table postgres_changes binding on payment_board:
//   • NotificationHandler — needs card IDS (card_<id> chat threads to badge);
//   • CustomerReplyAlerts — needs id / order number / customer name / owner of
//     the live (not archived) cards, to label and route an alert;
//   • the Messages page   — needs id / order number / customer name.
// None of them needs the heavy row (emails_sent + activity jsonb). With the
// flag on they share this: one thin read, kept current by `team:board` hints
// (one thin row fetched per change), one channel, ref-counted — it exists only
// while at least one of them is mounted. If the channel cannot be joined,
// subscribeBoard falls back to a single shared postgres_changes binding.
import { UserRole } from '../../types';
import { subscribeBoard, isSchemaGap, type BoardCard } from '../paymentBoard';
import { isSuperTier } from '../roles';

const COLUMNS = ['order_number', 'customer_name', 'assigned_to', 'archived'];
const COLUMNS_PRE_0071 = ['order_number', 'customer_name', 'assigned_to'];

/** The roles migration 0077 lets join `team:board` (route /payment_approval). */
export const canJoinBoardTopic = (role: string | null | undefined): boolean =>
  isSuperTier(role) || role === UserRole.ADMIN || role === UserRole.DISPATCH;

interface Listener { onCards: (cards: BoardCard[]) => void; onError?: (e: Error) => void }

const listeners = new Set<Listener>();
let cards: BoardCard[] | null = null;
let stop: (() => void) | null = null;
// Replayed to a late subscriber so its "first answer" flag can never hang.
let lastError: Error | null = null;

function start(role: string | null | undefined, columns: string[]): void {
  stop = subscribeBoard(
    (list) => { cards = list; lastError = null; listeners.forEach((l) => l.onCards(list)); },
    (e) => {
      if (columns === COLUMNS && isSchemaGap(e)) { stop?.(); start(role, COLUMNS_PRE_0071); return; }
      lastError = e;
      listeners.forEach((l) => l.onError?.(e));
    },
    { columns, liveV2: canJoinBoardTopic(role) },
  );
}

/** Thin cards (id, orderNumber, customerName, assignedTo, archived), newest
 *  first, archived included — filter `!c.archived` for the live board. */
export function subscribeBoardDirectory(
  role: string | null | undefined,
  onCards: (cards: BoardCard[]) => void,
  onError?: (e: Error) => void,
): () => void {
  const l: Listener = { onCards, onError };
  listeners.add(l);
  if (!stop) start(role, COLUMNS);
  else if (cards) onCards(cards);
  else if (lastError) onError?.(lastError);
  return () => {
    if (!listeners.delete(l)) return;
    if (listeners.size === 0) { stop?.(); stop = null; cards = null; lastError = null; }
  };
}

/** Test seam. */
export function _resetBoardDirectory(): void {
  stop?.(); stop = null; cards = null; lastError = null; listeners.clear();
}
